/**
 * ScoreEntry — the shared badminton score grid (teams as rows, G1·G2·G3 as
 * columns) with per-set validation, autofill of the winning score, hiding of an
 * unreachable deciding game, and an auto-derived match winner. All scoring rules
 * come from the shared `@sulis-liiga/common` badminton module.
 *
 * Used by both the result-entry page (a scheduled game) and the record-a-game
 * page (a played game recorded in one step). The component OWNS the score grid
 * state and reports the derived result to the parent via `onChange`; the parent
 * maps the positional a/b results back to team ids and owns the submit UI.
 *
 * The two teams are held positionally as `a`/`b` (matching the game's `teamIds`
 * order) — there is no home/away distinction.
 *
 * Each cell's value is held as a STRING so an empty cell (`''`, not yet entered)
 * is distinct from a genuine `0`: `21–0` is a legal game, and a set may end 0 for
 * one side (or 0–0 in a retirement). The numeric scores used by the shared scoring
 * logic are derived from those strings ('' → 0); a set counts as "played" once
 * either of its cells has been entered, not merely when a number is non-zero.
 *
 * ## Retirement
 * A game can end in a retirement (a player could not continue — injury,
 * withdrawal, or concession). Ticking "ended in a retirement" and choosing which
 * team retired relaxes the grid: the winner is forced to the non-retiring team
 * (never derived from the scores), the final set may be left incomplete, and the
 * grid may even be left empty (a walkover). See SystemRequirements §12.
 */
import React, { useEffect, useMemo, useState } from 'react';
import type { PointSystem, SetPair } from '@sulis-liiga/common';
import {
  rulesFor,
  validateSet,
  deriveMatchWinner,
  countSetWins,
  winnerScoreForLoser,
  visibleSetCount,
} from '@sulis-liiga/common';

export interface ScoreEntryResult {
  /** Games (sets) won, positional { a, b }. Counts completed games only. */
  setWins: { a: number; b: number };
  /** Derived match winner ('a' | 'b'), or null when undetermined. */
  winner: 'a' | 'b' | null;
  /** The played sets (at least one cell entered), positional { a, b }. */
  sets: SetPair[];
  /** True if any visible set has a validation error. */
  hasErrors: boolean;
  /** True when a winner is determined and there are no validation errors. */
  isComplete: boolean;
  /**
   * Retirement state. `isRetirement` is set once the box is ticked; `retiredSide`
   * is the team that retired ('a' | 'b') once chosen. In retirement mode `winner`
   * is the non-retiring side and the parent submits `retiredTeamId`.
   */
  retirement: { isRetirement: boolean; retiredSide: 'a' | 'b' | null };
}

interface ScoreEntryProps {
  /** Point system for this game's division (defaults to 21-point). */
  pointSystem?: PointSystem;
  teamALabel: string;
  teamBLabel: string;
  /**
   * Optional scores to pre-fill the grid with (positional { a, b }, one entry per
   * played game), e.g. when editing/correcting an existing result. Omit for a
   * fresh entry. Sets beyond the resolved best-of are ignored.
   */
  initialSets?: SetPair[];
  /**
   * Optional initial retirement state (whether the game was a retirement and which
   * side retired), paired with `initialSets` when editing a recorded retirement.
   */
  initialRetirement?: { isRetirement: boolean; retiredSide: 'a' | 'b' | null };
  /**
   * Notified whenever the entered scores (and the derived result) change. Pass a
   * stable reference (e.g. a `useCallback`) — it is intentionally excluded from
   * the notify effect's deps so the effect fires on score changes only.
   */
  onChange: (result: ScoreEntryResult) => void;
}

const ScoreEntry: React.FC<ScoreEntryProps> = ({
  pointSystem,
  teamALabel,
  teamBLabel,
  initialSets,
  initialRetirement,
  onChange,
}) => {
  // `rulesFor` returns a stable reference per system, so it is safe in memo deps.
  const rules = rulesFor(pointSystem);

  // Score state: each cell is a STRING so '' (not entered) stays distinct from a
  // genuine '0'. Two teams (a, b — positional) × best-of games. Pre-filled from
  // `initialSets` when editing an existing result, else all blank.
  const [cells, setCells] = useState<Array<{ a: string; b: string }>>(() => {
    const blank = Array.from({ length: rules.bestOf }, () => ({ a: '', b: '' }));
    (initialSets ?? []).forEach((s, i) => {
      if (i < blank.length) blank[i] = { a: String(s.a), b: String(s.b) };
    });
    return blank;
  });
  // Which cell (per set) the app auto-filled as the WINNER, so we keep updating it
  // as the loser is typed (digit by digit) but stop once it is edited by hand.
  const [autoCell, setAutoCell] = useState<Array<'a' | 'b' | null>>(() =>
    Array.from({ length: rules.bestOf }, () => null),
  );
  // Retirement: whether the game ended in a retirement, and which team retired.
  const [isRetirement, setIsRetirement] = useState(initialRetirement?.isRetirement ?? false);
  const [retiredSide, setRetiredSide] = useState<'a' | 'b' | null>(
    initialRetirement?.retiredSide ?? null,
  );

  // Numeric projection of the cell strings ('' → 0), clamped to the legal range,
  // for all the shared scoring logic (winner, set wins, validation, deciding-set).
  const scores: SetPair[] = useMemo(
    () =>
      cells.map(c => {
        const toNum = (s: string): number => {
          const n = parseInt(s, 10);
          return s === '' || isNaN(n) ? 0 : Math.max(0, Math.min(rules.cap, n));
        };
        return { a: toNum(c.a), b: toNum(c.b) };
      }),
    [cells, rules.cap],
  );

  // Resize the grid if the resolved system's best-of differs from the initial default.
  useEffect(() => {
    setCells(prev => {
      if (prev.length === rules.bestOf) return prev;
      return Array.from({ length: rules.bestOf }, (_, i) => prev[i] ?? { a: '', b: '' });
    });
    setAutoCell(prev => {
      if (prev.length === rules.bestOf) return prev;
      return Array.from({ length: rules.bestOf }, (_, i) => prev[i] ?? null);
    });
  }, [rules.bestOf]);

  // Clamp a raw input to a legal score string; '' stays '' (an un-entered cell).
  const sanitize = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (digits === '') return '';
    return String(Math.max(0, Math.min(rules.cap, parseInt(digits, 10))));
  };

  const updateScore = (setIndex: number, team: 'a' | 'b', value: string) => {
    const str = sanitize(value);
    const other = team === 'a' ? 'b' : 'a';
    const updated = cells.map((c, i) => (i === setIndex ? { ...c } : c));
    updated[setIndex][team] = str;
    const nextAuto = [...autoCell];

    // In a retirement the scores are the exact points played up to the stoppage
    // (the last set may be incomplete), so we neither autofill the winning score
    // nor hide an "unreachable" deciding game — every column stays editable.
    if (isRetirement) {
      nextAuto[setIndex] = null;
      setCells(updated);
      setAutoCell(nextAuto);
      return;
    }

    if (autoCell[setIndex] === team) {
      // Editing the auto-filled cell → entering the winning score by hand.
      nextAuto[setIndex] = null;
    } else {
      // Entering the LOSING score → re-derive the WINNING score on every keystroke.
      // A loser of 0 is legal (0 → 21), so key off "entered", not "non-zero".
      if (str !== '') {
        const w = winnerScoreForLoser(parseInt(str, 10), rules);
        if (w != null) {
          updated[setIndex][other] = String(w);
          nextAuto[setIndex] = other;
        }
      } else if (nextAuto[setIndex] === other) {
        updated[setIndex][other] = '';
        nextAuto[setIndex] = null;
      }
    }

    // Hide (clear) any deciding game that is no longer reachable (e.g. game 3 at 2-0).
    const toNum = (s: string): number => (s === '' ? 0 : parseInt(s, 10) || 0);
    const numeric = updated.map(c => ({ a: toNum(c.a), b: toNum(c.b) }));
    const visible = visibleSetCount(numeric, rules);
    for (let i = visible; i < updated.length; i++) {
      updated[i] = { a: '', b: '' };
      nextAuto[i] = null;
    }

    setCells(updated);
    setAutoCell(nextAuto);
  };

  // Whether a set has been touched (either cell entered) — a played set, even 0–0.
  const isEntered = (i: number): boolean => cells[i].a !== '' || cells[i].b !== '';

  // Games (sets) won across completed games — used for the "Games" column and, in
  // a normal result, to derive the winner.
  const setWins = useMemo(() => countSetWins(scores, rules), [scores, rules]);
  // In a retirement all best-of columns stay visible so any partial set can be
  // entered; otherwise the deciding game is hidden until the match is level.
  const visibleCount = useMemo(
    () => (isRetirement ? rules.bestOf : visibleSetCount(scores, rules)),
    [scores, rules, isRetirement],
  );

  // Winner: forced to the non-retiring side in a retirement, else derived.
  const winner: 'a' | 'b' | null = useMemo(() => {
    if (isRetirement) return retiredSide === 'a' ? 'b' : retiredSide === 'b' ? 'a' : null;
    return deriveMatchWinner(scores, rules);
  }, [isRetirement, retiredSide, scores, rules]);

  const setErrors = useMemo(
    () =>
      scores.map(({ a, b }, i) => {
        if (isRetirement) return null; // partial scores are allowed on a retirement
        if (i >= visibleCount) return null; // hidden deciding game
        if (a === 0 && b === 0) return null; // blank — nothing to validate yet
        return validateSet(a, b, rules);
      }),
    [scores, rules, visibleCount, isRetirement],
  );
  const hasErrors = setErrors.some(e => e !== null);

  // Report the derived result to the parent whenever the inputs change. A set is
  // reported once either cell has been entered — so a genuine 0 (e.g. 21–0, or a
  // 0-for-one-side set on a retirement) is included, not dropped as "blank".
  useEffect(() => {
    const sets = scores.filter((_, i) => isEntered(i));
    const isComplete = isRetirement ? retiredSide != null : !hasErrors && winner != null;
    onChange({
      setWins,
      winner,
      sets,
      hasErrors,
      isComplete,
      retirement: { isRetirement, retiredSide },
    });
    // onChange is intentionally omitted — notify on input changes only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cells, rules.system, isRetirement, retiredSide]);

  const visibleCells = cells.slice(0, visibleCount);
  const winnerLabel = winner === 'a' ? teamALabel : winner === 'b' ? teamBLabel : null;

  return (
    <section className="mt-4 bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-700">Game scores</h2>
        <span className="text-xs text-gray-400">
          Best of {rules.bestOf} · to {rules.target} · win by 2 · cap {rules.cap}
        </span>
      </div>

      {/* Validation errors */}
      {setErrors.map(
        (err, i) =>
          err && (
            <div key={i} className="mt-2 rounded-md bg-danger-50 p-2" role="alert">
              <p className="text-xs text-danger-600">
                Game {i + 1}: {err}
              </p>
            </div>
          ),
      )}

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="text-gray-500">
            <tr>
              <th className="text-left px-3 py-1 font-medium w-32" />
              {visibleCells.map((_, i) => (
                <th key={i} className="px-2 py-1 font-medium text-center">
                  G{i + 1}
                  {i + 1 > rules.setsToWin && (
                    <span className="text-gray-300 font-normal text-xs"> (decider)</span>
                  )}
                </th>
              ))}
              <th className="px-3 py-1 font-medium text-center">Games</th>
            </tr>
          </thead>
          <tbody>
            {/* Team A row */}
            <tr className={`border-t border-gray-100 ${winner === 'a' ? 'bg-success-50' : ''}`}>
              <td className="px-3 py-2 font-medium whitespace-nowrap text-gray-900">
                {teamALabel}
              </td>
              {visibleCells.map((c, i) => (
                <td key={i} className="px-2 py-2 text-center">
                  <input
                    type="number"
                    min={0}
                    max={rules.cap}
                    value={c.a}
                    onChange={e => updateScore(i, 'a', e.target.value)}
                    aria-label={`${teamALabel} score game ${i + 1}`}
                    className={`w-16 text-center rounded-md border px-2 py-2 text-lg font-semibold focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      setErrors[i] ? 'border-danger-400' : 'border-gray-300'
                    }`}
                  />
                </td>
              ))}
              <td className="px-3 py-2 text-center font-bold text-success-800">{setWins.a}</td>
            </tr>

            {/* Team B row */}
            <tr className={`border-t border-gray-100 ${winner === 'b' ? 'bg-success-50' : ''}`}>
              <td className="px-3 py-2 font-medium whitespace-nowrap text-gray-900">
                {teamBLabel}
              </td>
              {visibleCells.map((c, i) => (
                <td key={i} className="px-2 py-2 text-center">
                  <input
                    type="number"
                    min={0}
                    max={rules.cap}
                    value={c.b}
                    onChange={e => updateScore(i, 'b', e.target.value)}
                    aria-label={`${teamBLabel} score game ${i + 1}`}
                    className={`w-16 text-center rounded-md border px-2 py-2 text-lg font-semibold focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      setErrors[i] ? 'border-danger-400' : 'border-gray-300'
                    }`}
                  />
                </td>
              ))}
              <td className="px-3 py-2 text-center font-bold text-gray-500">{setWins.b}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Retirement */}
      <div className="mt-4 border-t border-gray-100 pt-4">
        <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={isRetirement}
            onChange={e => {
              setIsRetirement(e.target.checked);
              if (!e.target.checked) setRetiredSide(null);
            }}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span>
            Game ended in a <strong>retirement</strong> (a player could not continue — injury,
            withdrawal, or concession).
          </span>
        </label>

        {isRetirement && (
          <div className="mt-3 pl-6">
            <p className="text-sm font-medium text-gray-700">Which team retired?</p>
            <p className="text-xs text-gray-500">
              Enter the score up to the point of retirement (the last set may be incomplete), or
              leave the grid empty for a walkover. The other team wins.
            </p>
            <div className="mt-2 flex flex-col gap-2">
              {(['a', 'b'] as const).map(side => (
                <label key={side} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="retiredSide"
                    checked={retiredSide === side}
                    onChange={() => setRetiredSide(side)}
                    className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-gray-900">{side === 'a' ? teamALabel : teamBLabel}</span>
                  <span className="text-gray-400">retired</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Winner */}
      <p className="mt-3 text-sm text-center text-gray-600">
        Winner:{' '}
        {winnerLabel ? (
          <span className="font-semibold text-success-800">
            {winnerLabel}
            {isRetirement && <span className="font-normal text-gray-500"> (opponent retired)</span>}
          </span>
        ) : (
          <span className="text-gray-400">
            {isRetirement ? 'select which team retired' : 'auto-determined from game scores'}
          </span>
        )}
      </p>
    </section>
  );
};

export default ScoreEntry;
